/**
 * Blog Admin Page - Copie fidèle de CDFPI
 *
 * Features:
 * - Image upload avec drag & drop
 * - Gestion articles avec thumbnails
 * - Gestion catégories avec images
 * - Statuts: Publié / Planifié / Brouillon
 * - Sélecteur d'auteur
 * - Section SEO complète
 * - Publication programmée
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Icons } from '../../components/Icons';
import './BlogAdminPage.css';

// Types
interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string;
  cover_image: string | null;
  is_active: boolean;
  icon?: string;
}

interface BlogArticle {
  id: string;
  category_id: string;
  author_id: string | null;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  cover_image: string | null;
  tags: string[];
  reading_time: number;
  is_featured: boolean;
  is_published: boolean;
  published_at: string | null;
  scheduled_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[];
  views_count: number;
  created_at: string;
  category?: BlogCategory;
  author?: Author;
}

interface Author {
  id: string;
  username: string;
  email: string;
  avatar_url: string | null;
}

// Image Upload Component with drag & drop
function ImageUpload({
  value,
  onChange,
  label = 'Image',
}: {
  value: string;
  onChange: (url: string) => void;
  label?: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    if (!file) return;

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        onChange(e.target?.result as string);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleUpload(file);
    }
  };

  const handleRemove = () => {
    onChange('');
  };

  return (
    <div className="image-upload">
      <div className="image-upload__header">
        <label className="image-upload__label">{label}</label>
      </div>

      {value ? (
        <div className="image-upload__preview">
          <img src={value} alt="Preview" className="image-upload__image" />
          <div className="image-upload__overlay">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="image-upload__btn image-upload__btn--change"
            >
              Changer
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="image-upload__btn image-upload__btn--remove"
            >
              Supprimer
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="image-upload__input-hidden"
          />
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`image-upload__dropzone ${dragOver ? 'image-upload__dropzone--dragover' : ''}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="image-upload__input-hidden"
          />
          {uploading ? (
            <div className="image-upload__uploading">
              <Icons.Loader size={32} className="spinning" />
              <p>Chargement...</p>
            </div>
          ) : (
            <div className="image-upload__placeholder">
              <div className="image-upload__icon">
                <Icons.Image size={24} />
              </div>
              <p className="image-upload__text">
                Glissez une image ou cliquez pour parcourir
              </p>
              <p className="image-upload__hint">
                JPG, PNG, GIF, WebP • Max 10 Mo
              </p>
            </div>
          )}
        </div>
      )}

      <div className="image-upload__url">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Ou entrez une URL d'image"
          className="image-upload__url-input"
        />
      </div>
    </div>
  );
}

export function BlogAdminPage() {
  const [activeTab, setActiveTab] = useState<'articles' | 'categories'>('articles');
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingItem, setEditingItem] = useState<BlogArticle | BlogCategory | null>(null);
  const [editorType, setEditorType] = useState<'article' | 'category'>('article');
  const [saving, setSaving] = useState(false);

  const [articleForm, setArticleForm] = useState({
    category_id: '',
    author_id: '',
    slug: '',
    title: '',
    excerpt: '',
    content: '',
    cover_image: '',
    tags: '',
    is_featured: false,
    is_published: false,
    scheduled_at: '',
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: '',
    color: '#6b7280',
    cover_image: '',
    is_active: true,
  });

  const getAdminKey = () => localStorage.getItem('admin_key') || '';

  const apiCall = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const adminKey = getAdminKey();
    const response = await fetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminKey}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'API Error' }));
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        apiCall('/api/admin/blog?action=articles'),
        apiCall('/api/admin/blog?action=categories'),
      ]);

      setArticles(articlesRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateArticle = () => {
    setEditingItem(null);
    setEditorType('article');
    setArticleForm({
      category_id: categories[0]?.id || '',
      author_id: '',
      slug: '',
      title: '',
      excerpt: '',
      content: '',
      cover_image: '',
      tags: '',
      is_featured: false,
      is_published: false,
      scheduled_at: '',
      seo_title: '',
      seo_description: '',
      seo_keywords: '',
    });
    setShowEditor(true);
  };

  const handleCreateCategory = () => {
    setEditingItem(null);
    setEditorType('category');
    setCategoryForm({
      name: '',
      slug: '',
      description: '',
      color: '#6b7280',
      cover_image: '',
      is_active: true,
    });
    setShowEditor(true);
  };

  const handleEditArticle = (article: BlogArticle) => {
    setEditingItem(article);
    setEditorType('article');
    let scheduledAtLocal = '';
    if (article.scheduled_at) {
      const date = new Date(article.scheduled_at);
      scheduledAtLocal = date.toISOString().slice(0, 16);
    }
    setArticleForm({
      category_id: article.category_id || '',
      author_id: article.author_id || '',
      slug: article.slug,
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      cover_image: article.cover_image || '',
      tags: article.tags?.join(', ') || '',
      is_featured: article.is_featured,
      is_published: article.is_published,
      scheduled_at: scheduledAtLocal,
      seo_title: article.seo_title || '',
      seo_description: article.seo_description || '',
      seo_keywords: article.seo_keywords?.join(', ') || '',
    });
    setShowEditor(true);
  };

  const handleEditCategory = (category: BlogCategory) => {
    setEditingItem(category);
    setEditorType('category');
    setCategoryForm({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      color: category.color || '#6b7280',
      cover_image: category.cover_image || '',
      is_active: category.is_active,
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editorType === 'article') {
        let scheduledAtISO = null;
        if (articleForm.scheduled_at && !articleForm.is_published) {
          const localDate = new Date(articleForm.scheduled_at);
          scheduledAtISO = localDate.toISOString();
        }

        const payload = {
          ...articleForm,
          author_id: articleForm.author_id || null,
          tags: articleForm.tags.split(',').map(t => t.trim()).filter(Boolean),
          seo_keywords: articleForm.seo_keywords.split(',').map(k => k.trim()).filter(Boolean),
          cover_image: articleForm.cover_image || null,
          seo_title: articleForm.seo_title || null,
          seo_description: articleForm.seo_description || null,
          scheduled_at: scheduledAtISO,
          category_id: articleForm.category_id || null,
        };

        if (editingItem) {
          await apiCall(`/api/admin/blog?action=article&id=${editingItem.id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
          });
        } else {
          await apiCall('/api/admin/blog?action=articles', {
            method: 'POST',
            body: JSON.stringify(payload),
          });
        }
      } else {
        const payload = {
          ...categoryForm,
          cover_image: categoryForm.cover_image || null,
        };

        if (editingItem) {
          await apiCall(`/api/admin/blog?action=category&id=${editingItem.id}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
          });
        } else {
          await apiCall('/api/admin/blog?action=categories', {
            method: 'POST',
            body: JSON.stringify(payload),
          });
        }
      }

      await fetchData();
      setShowEditor(false);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, type: 'article' | 'category') => {
    if (!confirm(`Supprimer ${type === 'article' ? 'cet article' : 'cette catégorie'} ?`)) return;

    try {
      await apiCall(`/api/admin/blog?action=${type}&id=${id}`, { method: 'DELETE' });
      await fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleTogglePublish = async (article: BlogArticle) => {
    try {
      await apiCall(`/api/admin/blog?action=article&id=${article.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_published: !article.is_published }),
      });
      await fetchData();
    } catch (error) {
      console.error('Error toggling publish:', error);
    }
  };

  const filteredArticles = articles.filter(a =>
    a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr: string | null, includeTime = false) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (includeTime) {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="blog-admin__loading">
        <Icons.Loader size={32} className="spinning" />
      </div>
    );
  }

  return (
    <div className="blog-admin blog-admin--light">
      {/* Header */}
      <div className="blog-admin__header">
        <div className="blog-admin__header-info">
          <h1 className="blog-admin__title">Blog</h1>
          <p className="blog-admin__subtitle">Gérez vos articles et catégories</p>
        </div>
        <div className="blog-admin__header-actions">
          <button
            onClick={handleCreateCategory}
            className="blog-admin__btn blog-admin__btn--secondary"
          >
            <Icons.Folder size={18} />
            Catégorie
          </button>
          <button
            onClick={handleCreateArticle}
            className="blog-admin__btn blog-admin__btn--primary"
          >
            <Icons.Plus size={18} />
            Article
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="blog-admin__tabs">
        <button
          onClick={() => setActiveTab('articles')}
          className={`blog-admin__tab ${activeTab === 'articles' ? 'blog-admin__tab--active' : ''}`}
        >
          Articles ({articles.length})
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`blog-admin__tab ${activeTab === 'categories' ? 'blog-admin__tab--active' : ''}`}
        >
          Catégories ({categories.length})
        </button>
      </div>

      {/* Search */}
      <div className="blog-admin__search">
        <Icons.Search size={20} className="blog-admin__search-icon" />
        <input
          type="text"
          placeholder={`Rechercher ${activeTab === 'articles' ? 'un article' : 'une catégorie'}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="blog-admin__search-input"
        />
      </div>

      {/* Content - Articles */}
      {activeTab === 'articles' ? (
        <div className="blog-admin__table-wrapper">
          <table className="blog-admin__table">
            <thead>
              <tr>
                <th>Article</th>
                <th>Catégorie</th>
                <th>Statut</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredArticles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="blog-admin__empty">
                    <Icons.FileText size={48} />
                    <p>Aucun article trouvé</p>
                  </td>
                </tr>
              ) : (
                filteredArticles.map((article) => {
                  const cat = categories.find(c => c.id === article.category_id);
                  return (
                    <tr key={article.id}>
                      <td>
                        <div className="blog-admin__article-cell">
                          {article.cover_image ? (
                            <img
                              src={article.cover_image}
                              alt=""
                              className="blog-admin__article-thumb"
                            />
                          ) : (
                            <div className="blog-admin__article-thumb blog-admin__article-thumb--placeholder">
                              <Icons.FileText size={24} />
                            </div>
                          )}
                          <div className="blog-admin__article-info">
                            <div className="blog-admin__article-title-row">
                              <span className="blog-admin__article-title">{article.title}</span>
                              {article.is_featured && (
                                <Icons.Star size={16} className="blog-admin__featured-star" />
                              )}
                            </div>
                            <span className="blog-admin__article-excerpt">{article.excerpt}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        {cat ? (
                          <span
                            className="blog-admin__category-badge"
                            style={{
                              backgroundColor: `${cat.color}20`,
                              color: cat.color
                            }}
                          >
                            {cat.name}
                          </span>
                        ) : (
                          <span className="blog-admin__category-badge blog-admin__category-badge--none">
                            Sans catégorie
                          </span>
                        )}
                      </td>
                      <td>
                        {article.is_published ? (
                          <span className="blog-admin__status blog-admin__status--published">
                            <Icons.Eye size={14} />
                            Publié
                          </span>
                        ) : article.scheduled_at ? (
                          <span className="blog-admin__status blog-admin__status--scheduled">
                            <Icons.Clock size={14} />
                            Planifié
                          </span>
                        ) : (
                          <span className="blog-admin__status blog-admin__status--draft">
                            <Icons.EyeOff size={14} />
                            Brouillon
                          </span>
                        )}
                      </td>
                      <td>
                        {article.scheduled_at && !article.is_published ? (
                          <span className="blog-admin__date blog-admin__date--scheduled">
                            <Icons.Clock size={16} />
                            {formatDate(article.scheduled_at, true)}
                          </span>
                        ) : (
                          <span className="blog-admin__date">
                            <Icons.Calendar size={16} />
                            {formatDate(article.created_at)}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="blog-admin__actions">
                          <button
                            onClick={() => handleTogglePublish(article)}
                            className={`blog-admin__action-btn ${article.is_published ? 'blog-admin__action-btn--unpublish' : 'blog-admin__action-btn--publish'}`}
                            title={article.is_published ? 'Dépublier' : 'Publier'}
                          >
                            {article.is_published ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
                          </button>
                          <button
                            onClick={() => handleEditArticle(article)}
                            className="blog-admin__action-btn blog-admin__action-btn--edit"
                            title="Modifier"
                          >
                            <Icons.Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(article.id, 'article')}
                            className="blog-admin__action-btn blog-admin__action-btn--delete"
                            title="Supprimer"
                          >
                            <Icons.Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="blog-admin__categories-grid">
          {filteredCategories.length === 0 ? (
            <div className="blog-admin__empty blog-admin__empty--full">
              <Icons.Folder size={48} />
              <p>Aucune catégorie trouvée</p>
            </div>
          ) : (
            filteredCategories.map((category) => (
              <div key={category.id} className="blog-admin__category-card">
                {category.cover_image ? (
                  <div className="blog-admin__category-image">
                    <img src={category.cover_image} alt={category.name} />
                    <div className="blog-admin__category-gradient" />
                  </div>
                ) : (
                  <div
                    className="blog-admin__category-image blog-admin__category-image--placeholder"
                    style={{ backgroundColor: `${category.color}20` }}
                  >
                    <Icons.Tag size={48} style={{ color: category.color, opacity: 0.5 }} />
                  </div>
                )}

                <div className="blog-admin__category-content">
                  <div className="blog-admin__category-header">
                    <div
                      className="blog-admin__category-icon-wrapper"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <Icons.Tag size={20} style={{ color: category.color }} />
                    </div>
                    <div className="blog-admin__category-actions">
                      <button
                        onClick={() => handleEditCategory(category)}
                        className="blog-admin__action-btn blog-admin__action-btn--edit"
                      >
                        <Icons.Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id, 'category')}
                        className="blog-admin__action-btn blog-admin__action-btn--delete"
                      >
                        <Icons.Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <h2 className="blog-admin__category-name">{category.name}</h2>
                  <p className="blog-admin__category-description">
                    {category.description || 'Pas de description'}
                  </p>
                  <div className="blog-admin__category-footer">
                    <code className="blog-admin__category-slug">{category.slug}</code>
                    <span className={`blog-admin__category-status ${category.is_active ? 'blog-admin__category-status--active' : ''}`}>
                      {category.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <div className="blog-admin__modal-overlay">
          <div className="blog-admin__modal">
            <div className="blog-admin__modal-header">
              <h2>
                {editingItem ? 'Modifier' : 'Nouveau'} {editorType === 'article' ? 'article' : 'catégorie'}
              </h2>
              <button onClick={() => setShowEditor(false)} className="blog-admin__modal-close">
                <Icons.X size={20} />
              </button>
            </div>

            <div className="blog-admin__modal-body">
              {editorType === 'article' ? (
                <>
                  <div className="blog-admin__form-row">
                    <div className="blog-admin__form-group">
                      <label>Titre *</label>
                      <input
                        type="text"
                        value={articleForm.title}
                        onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                      />
                    </div>
                    <div className="blog-admin__form-group">
                      <label>Slug *</label>
                      <input
                        type="text"
                        value={articleForm.slug}
                        onChange={(e) => setArticleForm({
                          ...articleForm,
                          slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
                        })}
                      />
                    </div>
                  </div>

                  <div className="blog-admin__form-row">
                    <div className="blog-admin__form-group">
                      <label>Catégorie *</label>
                      <select
                        aria-label="Catégorie"
                        value={articleForm.category_id}
                        onChange={(e) => setArticleForm({ ...articleForm, category_id: e.target.value })}
                      >
                        <option value="">Sélectionner une catégorie</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="blog-admin__form-group">
                      <label>Auteur</label>
                      <select
                        aria-label="Auteur"
                        value={articleForm.author_id}
                        onChange={(e) => setArticleForm({ ...articleForm, author_id: e.target.value })}
                      >
                        <option value="">Sélectionner un auteur</option>
                        {authors.map((author) => (
                          <option key={author.id} value={author.id}>
                            {author.username || author.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <ImageUpload
                    value={articleForm.cover_image}
                    onChange={(url) => setArticleForm({ ...articleForm, cover_image: url })}
                    label="Image de couverture"
                  />

                  <div className="blog-admin__form-group">
                    <label>Extrait *</label>
                    <textarea
                      value={articleForm.excerpt}
                      onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                      rows={2}
                    />
                  </div>

                  <div className="blog-admin__form-group">
                    <label>Contenu *</label>
                    <textarea
                      value={articleForm.content}
                      onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                      rows={10}
                      className="blog-admin__content-textarea"
                    />
                  </div>

                  <div className="blog-admin__form-group">
                    <label>Tags (séparés par virgule)</label>
                    <input
                      type="text"
                      value={articleForm.tags}
                      onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })}
                      placeholder="crypto, trading, bitcoin"
                    />
                  </div>

                  <div className="blog-admin__section">
                    <h3 className="blog-admin__section-title">SEO (Référencement)</h3>
                    <div className="blog-admin__form-group">
                      <label>Titre SEO</label>
                      <input
                        type="text"
                        value={articleForm.seo_title}
                        onChange={(e) => setArticleForm({ ...articleForm, seo_title: e.target.value })}
                        placeholder="Laissez vide pour utiliser le titre de l'article"
                      />
                      <span className="blog-admin__char-count">{articleForm.seo_title.length}/60 caractères recommandés</span>
                    </div>
                    <div className="blog-admin__form-group">
                      <label>Description SEO</label>
                      <textarea
                        value={articleForm.seo_description}
                        onChange={(e) => setArticleForm({ ...articleForm, seo_description: e.target.value })}
                        placeholder="Laissez vide pour utiliser l'extrait"
                        rows={2}
                      />
                      <span className="blog-admin__char-count">{articleForm.seo_description.length}/160 caractères recommandés</span>
                    </div>
                    <div className="blog-admin__form-group">
                      <label>Mots-clés SEO (séparés par virgule)</label>
                      <input
                        type="text"
                        value={articleForm.seo_keywords}
                        onChange={(e) => setArticleForm({ ...articleForm, seo_keywords: e.target.value })}
                        placeholder="crypto, bitcoin, investissement"
                      />
                    </div>
                  </div>

                  <div className="blog-admin__section">
                    <h4 className="blog-admin__section-title">Publication</h4>

                    <div className="blog-admin__checkboxes">
                      <label className="blog-admin__checkbox">
                        <input
                          type="checkbox"
                          checked={articleForm.is_published}
                          onChange={(e) => setArticleForm({
                            ...articleForm,
                            is_published: e.target.checked,
                            scheduled_at: e.target.checked ? '' : articleForm.scheduled_at
                          })}
                        />
                        <span>Publier maintenant</span>
                      </label>
                      <label className="blog-admin__checkbox">
                        <input
                          type="checkbox"
                          checked={articleForm.is_featured}
                          onChange={(e) => setArticleForm({ ...articleForm, is_featured: e.target.checked })}
                        />
                        <span>Mettre en avant</span>
                      </label>
                    </div>

                    {!articleForm.is_published && (
                      <div className="blog-admin__scheduled-box">
                        <div className="blog-admin__scheduled-header">
                          <Icons.Clock size={20} />
                          <h5>Planifier la publication</h5>
                        </div>
                        <p className="blog-admin__scheduled-hint">
                          L'article sera automatiquement publié à la date et heure sélectionnées.
                        </p>
                        <div className="blog-admin__scheduled-input">
                          <div className="blog-admin__form-group">
                            <label>Date et heure</label>
                            <input
                              type="datetime-local"
                              value={articleForm.scheduled_at}
                              onChange={(e) => setArticleForm({ ...articleForm, scheduled_at: e.target.value })}
                              min={new Date().toISOString().slice(0, 16)}
                            />
                          </div>
                          <div className="blog-admin__scheduled-buttons">
                            <button
                              type="button"
                              onClick={() => {
                                const tomorrow = new Date();
                                tomorrow.setDate(tomorrow.getDate() + 1);
                                tomorrow.setHours(18, 0, 0, 0);
                                setArticleForm({
                                  ...articleForm,
                                  scheduled_at: tomorrow.toISOString().slice(0, 16)
                                });
                              }}
                              className="blog-admin__btn blog-admin__btn--small"
                            >
                              Demain 18h
                            </button>
                            {articleForm.scheduled_at && (
                              <button
                                type="button"
                                onClick={() => setArticleForm({ ...articleForm, scheduled_at: '' })}
                                className="blog-admin__btn blog-admin__btn--text"
                              >
                                Annuler
                              </button>
                            )}
                          </div>
                        </div>
                        {articleForm.scheduled_at && (
                          <p className="blog-admin__scheduled-preview">
                            Publication prévue le {new Date(articleForm.scheduled_at).toLocaleDateString('fr-FR', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="blog-admin__form-row">
                    <div className="blog-admin__form-group">
                      <label>Nom *</label>
                      <input
                        type="text"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      />
                    </div>
                    <div className="blog-admin__form-group">
                      <label>Slug *</label>
                      <input
                        type="text"
                        value={categoryForm.slug}
                        onChange={(e) => setCategoryForm({
                          ...categoryForm,
                          slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
                        })}
                      />
                    </div>
                  </div>

                  <div className="blog-admin__form-group">
                    <label>Description</label>
                    <textarea
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                      rows={3}
                      placeholder="Description de la catégorie..."
                    />
                  </div>

                  <ImageUpload
                    value={categoryForm.cover_image}
                    onChange={(url) => setCategoryForm({ ...categoryForm, cover_image: url })}
                    label="Image de la catégorie"
                  />

                  <div className="blog-admin__form-row">
                    <div className="blog-admin__form-group">
                      <label>Couleur</label>
                      <div className="blog-admin__color-input">
                        <input
                          type="color"
                          value={categoryForm.color}
                          onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                          className="blog-admin__color-picker"
                        />
                        <input
                          type="text"
                          value={categoryForm.color}
                          onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="blog-admin__form-group blog-admin__form-group--checkbox">
                      <label className="blog-admin__checkbox">
                        <input
                          type="checkbox"
                          checked={categoryForm.is_active}
                          onChange={(e) => setCategoryForm({ ...categoryForm, is_active: e.target.checked })}
                        />
                        <span>Catégorie active</span>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="blog-admin__modal-footer">
              <button
                onClick={() => setShowEditor(false)}
                className="blog-admin__btn blog-admin__btn--cancel"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="blog-admin__btn blog-admin__btn--save"
              >
                {saving ? (
                  <>
                    <Icons.Loader size={16} className="spinning" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Icons.Save size={16} />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlogAdminPage;
